import { useRouteError } from 'react-router'
import { Button } from '@/components/ui/button'

/**
 * What a person sees when a page breaks.
 *
 * Without this, react-router renders its own fallback: the exception, a minified stack trace,
 * and a note addressed to "Hey developer 👋" explaining how to replace it. Everyone who is not
 * that developer reads a wall of code and learns nothing they can act on. Somebody demoing the
 * product reads it in front of a client.
 *
 * So: say that this page broke, say the rest of the app is fine, and give them the two moves
 * that actually help — reload, or go somewhere else. The technical detail stays available for
 * a bug report, but folded away rather than shouted.
 */
export function RouteError() {
  const error = useRouteError()
  const detail =
    error instanceof Error ? error.message : typeof error === 'string' ? error : null

  /*
   * A chunk that is gone, rather than a page that is broken.
   *
   * Every page is a separate file named by content hash, and a deploy removes the old ones —
   * so an app that was already open asks for a file that no longer exists. `lazyWithReload`
   * reloads once to pick up the new names; reaching here means that already happened and did
   * not help, which is worth saying plainly instead of blaming the page.
   */
  const staleBuild = detail
    ? /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(detail)
    : false

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-lg font-semibold text-foreground">
        {staleBuild ? 'Tapwork was updated' : 'This page didn’t load'}
      </h1>
      <p className="text-sm text-muted-foreground">
        {staleBuild
          ? 'A new version was released while you had this open, so this page was still asking for the old one. Reloading picks up the new version — nothing is wrong with your work.'
          : 'Something went wrong on our side, not yours. The rest of Tapwork is still working — try loading this page again, and let us know if it keeps happening.'}
      </p>

      <div className="flex gap-2">
        <Button onClick={() => window.location.reload()}>Try again</Button>
        <Button variant="ghost" onClick={() => (window.location.href = '/')}>
          Go to my work
        </Button>
      </div>

      {detail ? (
        <details className="w-full pt-2 text-left">
          <summary className="cursor-pointer text-xs text-text-muted">
            Details to include in a bug report
          </summary>
          <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-muted p-2 text-tiny break-words whitespace-pre-wrap text-muted-foreground">
            {detail}
          </pre>
        </details>
      ) : null}
    </div>
  )
}
